import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { globalLoader, handleServerValidations } from "../../../../utils/commonfunction";
import * as Yup from "yup";
import * as MatchManagamentApi from "../../services/match.managament.service";
import * as clubAdmin from "../../services/clubAdmin.service";
import CustomError from "../../../../utils/customError";

const POSITIONS = ["GS", "GA", "WA", "C", "WD", "GD", "GK"];

const MatchAdd = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [clubs, setClubs] = useState([]);
    const [teams, setTeams] = useState([]);
    const [scorers, setScorers] = useState([]);
    const [homePlayers, setHomePlayers] = useState([]);
    const [awayPlayers, setAwayPlayers] = useState([]);

    const breadcrumbs = [
        { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
        { title: t("Match Management"), url: "/admin/match-management/match/list/1" },
        { title: t("Create Match"), url: "" },
    ];

    useEffect(() => {
        clubAdmin.List({ limit: 1000, filters: { isProfileVerified: "verified", isBlocked: false } }).then((res) => {
            if (res.success) {
                const activeClubs = (res.data.list || []).filter(
                    (c) => !c.isDeleted && 
                           c.isDeleted !== "true" && 
                           !c.deletedAt && 
                           c.status !== "DELETED" && 
                           (c.isProfileVerified === "verified" || c.isProfileVerified === true || c.isProfileVerified === "true") && 
                           !c.isBlocked
                );
                setClubs(activeClubs);
            }
        });
    }, []);

    const fetchClubData = (clubId) => {
        if (!clubId) {
            setTeams([]);
            setScorers([]);
            return;
        }
        MatchManagamentApi.Teams(clubId).then((res) => {
            if (res.success) setTeams(res.data || []);
        });
        MatchManagamentApi.Scorers(clubId).then((res) => {
            if (res.success) setScorers(res.data || []);
        });
    };

    const fetchHomePlayers = (teamId) => {
        if (!teamId) {
            setHomePlayers([]);
            return;
        }
        MatchManagamentApi.TeamPlayers(teamId).then((res) => {
            if (res.success) setHomePlayers(res.data.players || []);
        });
    };

    const fetchAwayPlayers = (teamId) => {
        const teamObj = teams.find(t => t.name === teamId || t.id === teamId);
        if (!teamObj || !teamObj.id) {
            setAwayPlayers([]);
            return;
        }
        MatchManagamentApi.TeamPlayers(teamObj.id).then((res) => {
            if (res.success) setAwayPlayers(res.data.players || []);
        });
    };

    const formik = useFormik({
        initialValues: {
            clubAdminId: "",
            title: "",
            homeTeam: "",
            homeTeamsPlayers: {},
            homeTeamImpactPlayers: ["", "", "", "", ""],
            awayTeam: "",
            awayTeamsPlayers: {},
            awayTeamImpactPlayers: ["", "", "", "", ""],
            date: "",
            time: "",
            venueTitle: "",
            venueLocation: "",
            scorer: "",
            quarterLength: "15",
            totalQuarters: "4",
        },
        validationSchema: Yup.object({
            clubAdminId: Yup.string().required("Club is required"),
            title: Yup.string().required("Title is required"),
            homeTeam: Yup.string().required("Home Team is required"),
            awayTeam: Yup.string().required("Away Team is required"),
            date: Yup.string().required("Date is required"),
            time: Yup.string().required("Time is required"),
            venueTitle: Yup.string().required("Venue Title is required"),
            scorer: Yup.string().required("Scorer is required"),
        }),
        onSubmit: (values) => {
            // Clean up empty impact players
            const finalValues = {
                ...values,
                homeTeamImpactPlayers: values.homeTeamImpactPlayers.filter(id => id !== ""),
                awayTeamImpactPlayers: values.awayTeamImpactPlayers.filter(id => id !== ""),
            };

            globalLoader(true);
            setSubmitted(true);
            MatchManagamentApi.Add(finalValues).then((res) => {
                globalLoader(false);
                if (res.success) {
                    Swal.fire({
                        icon: "success",
                        text: res.message,
                        ...SWAL_SETTINGS,
                    });
                    navigate("/admin/match-management/match/list/1");
                } else {
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(res),
                        ...SWAL_SETTINGS,
                    });
                    setSubmitted(false);
                }
            }).catch(() => {
                globalLoader(false);
                setSubmitted(false);
            });
        }
    });

    const handleHomePositionChange = (pos, playerId) => {
        formik.setFieldValue("homeTeamsPlayers", {
            ...formik.values.homeTeamsPlayers,
            [pos]: playerId
        });
    };

    const handleAwayPositionChange = (pos, playerId) => {
        formik.setFieldValue("awayTeamsPlayers", {
            ...formik.values.awayTeamsPlayers,
            [pos]: playerId
        });
    };

    const handleImpactPlayerChange = (team, index, value) => {
        const key = team === "home" ? "homeTeamImpactPlayers" : "awayTeamImpactPlayers";
        const newArr = [...formik.values[key]];
        newArr[index] = value;
        formik.setFieldValue(key, newArr);
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <form onSubmit={formik.handleSubmit}>
                    <div className="card custom-card">
                        <div className="card-body">
                            <h6 className="main-content-label mb-4">Create New Match</h6>
                            
                            <div className="row">
                                <div className="col-md-4 form-group">
                                    <label className="form-label">{t("sidebar_link_club")} <span className="text-danger">*</span></label>
                                    <select
                                        name="clubAdminId"
                                        className="form-control form-select"
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            fetchClubData(e.target.value);
                                        }}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.clubAdminId}
                                    >
                                        <option value="">Select Club</option>
                                        {clubs.map((club) => (
                                            <option key={club.id} value={club.id}>
                                                {club.clubDetails?.name || club.firstName}
                                            </option>
                                        ))}
                                    </select>
                                    <CustomError name="clubAdminId" form={formik} />
                                </div>

                                <div className="col-md-8 form-group">
                                    <label className="form-label">{t("label_match_title")} <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        name="title"
                                        placeholder="Enter Match Title"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                    />
                                    <CustomError name="title" form={formik} />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 form-group">
                                    <label className="form-label">{t("label_home_team")} <span className="text-danger">*</span></label>
                                    <select
                                        name="homeTeam"
                                        className="form-control form-select"
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            fetchHomePlayers(e.target.value);
                                        }}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.homeTeam}
                                        disabled={!formik.values.clubAdminId}
                                    >
                                        <option value="">Select Home Team</option>
                                        {teams.map((team) => (
                                            <option key={team.id} value={team.id}>{team.name}</option>
                                        ))}
                                    </select>
                                    <CustomError name="homeTeam" form={formik} />
                                </div>

                                <div className="col-md-6 form-group">
                                    <label className="form-label">{t("label_away_team")} <span className="text-danger">*</span></label>
                                    <div className="input-group">
                                        <select
                                            name="awayTeam"
                                            className="form-control form-select"
                                            onChange={(e) => {
                                                formik.handleChange(e);
                                                fetchAwayPlayers(e.target.value);
                                            }}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.awayTeam}
                                            disabled={!formik.values.clubAdminId}
                                            style={{ flex: "1" }}
                                        >
                                            <option value="">Select Away Team</option>
                                            {teams.map((team) => (
                                                <option key={team.name} value={team.name}>{team.name}</option>
                                            ))}
                                        </select>
                                        <input
                                            type="text"
                                            placeholder="Manual Entry"
                                            className="form-control"
                                            onChange={(e) => formik.setFieldValue("awayTeam", e.target.value)}
                                            onBlur={formik.handleBlur}
                                            value={teams.some(t => t.name === formik.values.awayTeam) ? "" : formik.values.awayTeam}
                                            disabled={!formik.values.clubAdminId}
                                            style={{ flex: "1" }}
                                        />
                                    </div>
                                    <CustomError name="awayTeam" form={formik} />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-3 form-group">
                                    <label className="form-label">{t("label_date")} <span className="text-danger">*</span></label>
                                    <input
                                        type="date"
                                        name="date"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.date}
                                    />
                                    <CustomError name="date" form={formik} />
                                </div>

                                <div className="col-md-3 form-group">
                                    <label className="form-label">{t("label_time")} <span className="text-danger">*</span></label>
                                    <input
                                        type="time"
                                        name="time"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.time}
                                    />
                                    <CustomError name="time" form={formik} />
                                </div>

                                <div className="col-md-6 form-group">
                                    <label className="form-label">Scorer <span className="text-danger">*</span></label>
                                    <select
                                        name="scorer"
                                        className="form-control form-select"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.scorer}
                                        disabled={!formik.values.clubAdminId}
                                    >
                                        <option value="">Select Scorer</option>
                                        {scorers.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                                        ))}
                                    </select>
                                    <CustomError name="scorer" form={formik} />
                                </div>
                            </div>

                            <div className="row mt-3">
                                {/* Home Team Positions */}
                                <div className="col-lg-6">
                                    <div className="p-3 bg-light rounded border mb-4">
                                        <h6 className="fw-bold mb-3">Home Team Positions</h6>
                                        {POSITIONS.map(pos => (
                                            <div key={pos} className="row mb-2 align-items-center">
                                                <div className="col-3 fw-bold text-muted">{pos}</div>
                                                <div className="col-9">
                                                    <select
                                                        className="form-control form-select form-select-sm"
                                                        onChange={(e) => handleHomePositionChange(pos, e.target.value)}
                                                        value={formik.values.homeTeamsPlayers[pos] || ""}
                                                        disabled={homePlayers.length === 0}
                                                    >
                                                        <option value="">Select Player</option>
                                                        {homePlayers.map(p => (
                                                            <option key={p.id} value={p.id}>{p.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        ))}

                                        <h6 className="fw-bold mt-4 mb-3">Impact Players</h6>
                                        {[0,1,2,3,4].map(idx => (
                                            <div key={idx} className="row mb-2 align-items-center">
                                                <div className="col-3 text-muted small">Player {idx + 1}</div>
                                                <div className="col-9">
                                                    <select
                                                        className="form-control form-select form-select-sm"
                                                        value={formik.values.homeTeamImpactPlayers[idx]}
                                                        onChange={(e) => handleImpactPlayerChange("home", idx, e.target.value)}
                                                        disabled={homePlayers.length === 0}
                                                    >
                                                        <option value="">Select Player</option>
                                                        {homePlayers.map(p => (
                                                            <option key={p.id} value={p.id}>{p.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Away Team Positions */}
                                <div className="col-lg-6">
                                    <div className="p-3 bg-light rounded border mb-4">
                                        <h6 className="fw-bold mb-3">Away Team Positions</h6>
                                        {POSITIONS.map(pos => (
                                            <div key={pos} className="row mb-2 align-items-center">
                                                <div className="col-3 fw-bold text-muted">{pos}</div>
                                                <div className="col-9">
                                                    {awayPlayers.length > 0 ? (
                                                        <select
                                                            className="form-control form-select form-select-sm"
                                                            onChange={(e) => handleAwayPositionChange(pos, e.target.value)}
                                                            value={formik.values.awayTeamsPlayers[pos] || ""}
                                                        >
                                                            <option value="">Select Player</option>
                                                            {awayPlayers.map(p => (
                                                                <option key={p.id} value={p.id}>{p.name}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Player Name"
                                                            className="form-control form-control-sm"
                                                            value={formik.values.awayTeamsPlayers[pos] || ""}
                                                            onChange={(e) => handleAwayPositionChange(pos, e.target.value)}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        <h6 className="fw-bold mt-4 mb-3">Impact Players</h6>
                                        {[0,1,2,3,4].map(idx => (
                                            <div key={idx} className="row mb-2 align-items-center">
                                                <div className="col-3 text-muted small">Player {idx + 1}</div>
                                                <div className="col-9">
                                                    {awayPlayers.length > 0 ? (
                                                        <select
                                                            className="form-control form-select form-select-sm"
                                                            value={formik.values.awayTeamImpactPlayers[idx]}
                                                            onChange={(e) => handleImpactPlayerChange("away", idx, e.target.value)}
                                                        >
                                                            <option value="">Select Player</option>
                                                            {awayPlayers.map(p => (
                                                                <option key={p.id} value={p.id}>{p.name}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Player Name"
                                                            className="form-control form-control-sm"
                                                            value={formik.values.awayTeamImpactPlayers[idx]}
                                                            onChange={(e) => handleImpactPlayerChange("away", idx, e.target.value)}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-2">
                                <div className="col-md-6 form-group">
                                    <label className="form-label">{t("label_venue_title")} <span className="text-danger">*</span></label>
                                    <input
                                        type="text"
                                        name="venueTitle"
                                        placeholder="e.g. Court 1"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.venueTitle}
                                    />
                                    <CustomError name="venueTitle" form={formik} />
                                </div>

                                <div className="col-md-6 form-group">
                                    <label className="form-label">Venue Location</label>
                                    <input
                                        type="text"
                                        name="venueLocation"
                                        placeholder="Enter Venue Location"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.venueLocation}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-3 form-group">
                                    <label className="form-label">Quarter Length (mins)</label>
                                    <input
                                        type="number"
                                        name="quarterLength"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.quarterLength}
                                    />
                                </div>
                                <div className="col-md-3 form-group">
                                    <label className="form-label">Total Quarters</label>
                                    <input
                                        type="number"
                                        name="totalQuarters"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.totalQuarters}
                                    />
                                </div>
                            </div>

                            <div className="mt-4 pt-2 border-top">
                                <button
                                    type="submit"
                                    className="btn btn-primary me-2 px-4"
                                    style={{ background: "#008080", borderColor: "#008080" }}
                                    disabled={submitted}
                                >
                                    {t("btn_submit")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary px-4"
                                    onClick={() => navigate(-1)}
                                >
                                    {t("btn_cancel")}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default MatchAdd;
